import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Clock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import T from '@/components/T';

interface SubscriptionAlertBannerProps {
  className?: string;
}

const GRACE_PERIOD_DAYS = 7;

export const SubscriptionAlertBanner: React.FC<SubscriptionAlertBannerProps> = ({ className }) => {
  const navigate = useNavigate();
  const { plans, currentSubscription, loading } = useSubscriptionPlans();
  const { gracePeriodInfo, isExpired } = useSubscriptionAccess();

  if (loading || !currentSubscription) {
    return null;
  }

  const currentPlan = plans.find(plan => plan.id === currentSubscription.plan_id);
  
  const daysUntilExpiry = currentSubscription.expires_at 
    ? Math.ceil((new Date(currentSubscription.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Only show banner if expiring within 7 days OR in grace period
  const shouldShow = (daysUntilExpiry !== null && daysUntilExpiry <= 7) || 
                     (isExpired && gracePeriodInfo?.isWithinGracePeriod);
  
  if (!shouldShow) {
    return null;
  }

  const isInGracePeriod = isExpired && gracePeriodInfo?.isWithinGracePeriod;
  const isExpiredNoGrace = isExpired && !gracePeriodInfo?.isWithinGracePeriod;
  const isUrgent = (daysUntilExpiry !== null && daysUntilExpiry <= 3) || isInGracePeriod;

  const getDaysText = (days: number) => {
    if (days <= 0) return '';
    if (days === 1) return '1 день';
    if (days >= 2 && days <= 4) return `${days} дня`;
    return `${days} дней`;
  };

  // Determine banner style
  const getBannerStyle = () => {
    if (isExpiredNoGrace || (gracePeriodInfo?.gracePeriodDaysRemaining ?? 0) <= 1) {
      return 'border-destructive bg-destructive/15 shadow-destructive/20';
    }
    if (isInGracePeriod || (gracePeriodInfo?.gracePeriodDaysRemaining ?? 0) <= 3) {
      return 'border-orange-500 bg-orange-500/15 shadow-orange-500/20';
    }
    if (isUrgent) {
      return 'border-destructive bg-destructive/10 shadow-destructive/10';
    }
    return 'border-warning bg-warning/10 shadow-warning/10';
  };

  // Get icon color
  const getIconColor = () => {
    if (isExpiredNoGrace || (gracePeriodInfo?.gracePeriodDaysRemaining ?? 0) <= 1) return 'text-destructive';
    if (isInGracePeriod || (gracePeriodInfo?.gracePeriodDaysRemaining ?? 0) <= 3) return 'text-orange-500';
    if (isUrgent) return 'text-destructive';
    return 'text-warning';
  };

  return (
    <Alert 
      variant={isExpiredNoGrace ? "destructive" : "default"}
      className={`${className} ${getBannerStyle()} shadow-lg`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-5 w-5 mt-0.5 ${getIconColor()}`} />
        
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertTitle className="text-base font-semibold m-0">
              {isInGracePeriod ? (
                <T 
                  ru={`Подписка истекла! Осталось ${getDaysText(gracePeriodInfo?.gracePeriodDaysRemaining || 0)} для оплаты`} 
                  en={`Subscription expired! ${gracePeriodInfo?.gracePeriodDaysRemaining || 0} day(s) left to pay`}
                />
              ) : isExpiredNoGrace ? (
                <T ru="Подписка истекла!" en="Subscription expired!" />
              ) : (
                <T 
                  ru={`Подписка ${currentPlan?.name || 'Ваш план'} истекает через ${getDaysText(daysUntilExpiry || 0)}!`} 
                  en={`${currentPlan?.name || 'Your plan'} subscription expires in ${daysUntilExpiry} day${(daysUntilExpiry || 0) > 1 ? 's' : ''}!`}
                />
              )}
            </AlertTitle>
            
            {isInGracePeriod && (
              <Badge variant="outline" className="border-orange-500 text-orange-600 bg-orange-500/10 gap-1">
                <Clock className="h-3 w-3" />
                <T 
                  ru={`Grace период: ${gracePeriodInfo?.gracePeriodDaysRemaining} дн.`} 
                  en={`Grace period: ${gracePeriodInfo?.gracePeriodDaysRemaining} days`}
                />
              </Badge>
            )}
          </div>

          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-sm">
              {isInGracePeriod ? (
                <T 
                  ru={`После окончания grace-периода доступ будет полностью заблокирован. Оплатите сейчас, чтобы продолжить работу.`}
                  en={`After the grace period ends, access will be completely blocked. Pay now to continue working.`}
                />
              ) : isExpiredNoGrace ? (
                <T 
                  ru="Функционал заблокирован. Оплатите подписку для продолжения работы." 
                  en="Features are blocked. Pay to continue using the platform."
                />
              ) : (
                <>
                  <T ru="Дата истечения:" en="Expires on:" />{' '}
                  <span className="font-medium">
                    {format(new Date(currentSubscription.expires_at!), 'd MMMM yyyy', { locale: ru })}
                  </span>
                </>
              )}
            </span>
            
            <Button 
              size="sm" 
              variant={isExpiredNoGrace || isInGracePeriod ? "destructive" : isUrgent ? "destructive" : "default"}
              onClick={() => navigate('/subscription/payment')}
              className="shrink-0 gap-2 font-semibold"
            >
              <CreditCard className="h-4 w-4" />
              <T ru="Оплатить сейчас" en="Pay now" />
            </Button>
          </AlertDescription>
        </div>
      </div>
    </Alert>
  );
};
